import {
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HTTP_INTERCEPTORS,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { SignalRService } from './signalr.service';
import { isStaticAssetRequest } from '../utilities/http-request.utils';

/** Must match UserSecurity.OriginConnectionIdHeader on the backend. */
const ORIGIN_CONNECTION_ID_HEADER = 'X-SignalR-Connection-Id';

/**
 * Names this tab's SignalR connection on every outgoing request, so that a broadcast caused by a
 * request can be sent to the user's other sessions and skip the one that asked for it.
 *
 * Here rather than on the one endpoint that needs it, for the same reason the bearer token is: it
 * says which tab a request came from and nothing about what the request is for. Switching
 * organization is the first caller; anything else that pushes a per-user change gets the exclusion
 * without wiring a header through another service signature.
 *
 * Silent when there is no connection yet - the header is simply absent, and the server broadcasts
 * to everyone, which is the correct answer when we cannot say who "everyone else" is.
 */
@Injectable()
export class SignalRConnectionInterceptor implements HttpInterceptor {
  constructor(private signalRService: SignalRService) {}

  intercept(
    request: HttpRequest<unknown>,
    next: HttpHandler,
  ): Observable<HttpEvent<unknown>> {
    // Static assets (e.g. the SVGs angular-svg-icon fetches) aren't API calls and have no session
    // to name.
    if (isStaticAssetRequest(request.url)) {
      return next.handle(request);
    }

    const connectionId = this.signalRService.getConnectionId();

    return next.handle(
      connectionId
        ? request.clone({
            setHeaders: { [ORIGIN_CONNECTION_ID_HEADER]: connectionId },
          })
        : request,
    );
  }
}

export const signalRConnectionInterceptorProvider = {
  provide: HTTP_INTERCEPTORS,
  useClass: SignalRConnectionInterceptor,
  multi: true,
};
